// The projects a setting is on for, collapsed, then the rest to switch on: the one list
// shape every setting on the team settings page uses.
import { type ReactNode, useState } from "react";

import { Chevron } from "./Chevron";
import { plural } from "../routes/projectFilesCounts";
import type { Saving } from "../utils/projectEnablement";
import styles from "./ProjectEnablementList.module.css";

export interface EnablementRow {
  project_id: string;
  project_name: string;
  on: boolean;
  /** Shown beside the project's name, such as its status or what switching it on costs. */
  detail?: ReactNode;
}

export const ProjectEnablementList = ({
  rows,
  saving,
  onChange,
  enabledNote = "",
  offHeading,
  allOn,
  children,
}: {
  rows: EnablementRow[];
  saving: Saving;
  onChange: (projectId: string, checked: boolean) => void;
  enabledNote?: string;
  offHeading: "Disabled" | "Available";
  allOn: string;
  children?: ReactNode;
}) => {
  const [showOn, setShowOn] = useState(false);
  const on = rows.filter((r) => r.on);
  const off = rows.filter((r) => !r.on);
  const failedName =
    saving.mode === "per-tick"
      ? rows.find((r) => r.project_id === saving.failed)?.project_name
      : undefined;

  if (rows.length === 0) {
    return <p className={styles.muted}>No projects.</p>;
  }

  const checkbox = (r: EnablementRow) => {
    if (saving.mode === "per-tick") {
      return (
        <input
          type="checkbox"
          checked={r.on}
          disabled={saving.pending !== undefined}
          onChange={(e) => onChange(r.project_id, e.target.checked)}
        />
      );
    }
    if (r.on) return null;
    const picked = saving.selected.has(r.project_id);
    return (
      <input
        type="checkbox"
        checked={picked}
        disabled={saving.busy || (!picked && saving.selected.size >= saving.max)}
        onChange={(e) => onChange(r.project_id, e.target.checked)}
      />
    );
  };

  const row = (r: EnablementRow) => (
    <label key={r.project_id} className={styles.project}>
      {checkbox(r)}
      <span className={styles.projectName}>{r.project_name}</span>
      {r.detail}
      {saving.mode === "per-tick" && saving.pending === r.project_id && (
        <span className={styles.muted}>Saving…</span>
      )}
    </label>
  );

  return (
    <>
      {on.length > 0 && (
        <div className={styles.group}>
          <button
            type="button"
            className={styles.summary}
            aria-expanded={showOn}
            onClick={() => setShowOn(!showOn)}
          >
            <span aria-hidden="true">
              <Chevron tree open={showOn} />
            </span>{" "}
            Enabled for {plural(on.length, "project")}
            {enabledNote && ` ${enabledNote}`}
          </button>
          {showOn && <div className={styles.list}>{on.map(row)}</div>}
        </div>
      )}
      {off.length > 0 ? (
        <div className={styles.group}>
          <p className={styles.subheading}>
            {offHeading} for {plural(off.length, "project")}
          </p>
          <div className={styles.list}>{off.map(row)}</div>
        </div>
      ) : (
        <p className={styles.muted}>{allOn}</p>
      )}
      {children}
      {failedName && saving.mode === "per-tick" && saving.error && (
        <p className={styles.error}>
          Could not change {failedName}: {saving.error.message}
        </p>
      )}
    </>
  );
};
